import React, { Fragment } from "react";
import { MainLayout } from "../../components/Layout";
import Headline from "../../components/Headline";
import {
    WithdrawalRequestReason,
    WITHDRAWAL_REQUEST_REASONS_HEADLINE,
    WITHDRAWAL_REQUEST_REASONS
} from "../../generated/lib-domain/withdrawal-request-reason";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import SmartButton from "../../components/SmartButton";
import { isEmptyString } from "../../generated/lib-helpers/string";
import { isEmptyList } from "../../generated/lib-helpers/list";
import Modal from "../../components/Modal";
import "./styles.scss";

const schema = yup.object().shape({
    reasons: yup
        .array()
        .of(yup.string())
        .min(1, "退会理由が選択されていません")
        .required(),
    opinion: yup.string().max(300, "300文字以内で入力してください")
});

export type FormValues = {
    reasons: string[];
    opinion: string;
};

const defaultValues: FormValues = {
    reasons: [],
    opinion: ""
};

interface WithdrawalApplyProps {
    isLoading: boolean;
    isShowModal: boolean;
    onSubmitWithdrawalApply: (data: FormValues) => void;
    onClickConfirmModal: () => void;
    onClickModalClose: () => void;
}

const WithdrawalApply = (props: WithdrawalApplyProps) => {
    const {
        register,
        handleSubmit,
        watch,
        errors,
        formState: { isDirty, isValid }
    } = useForm({
        mode: "all",
        defaultValues,
        resolver: yupResolver(schema)
    });
    const { reasons: reasonsValue, opinion: opinionValue }: FormValues =
        watch();

    return (
        <>
            <MainLayout>
                <Headline>退会申請</Headline>
                <div className="withdrawal-apply">
                    <div className="withdrawal-apply__attention">
                        退会するとすべてのデータが削除されます。削除されたデータは復元できません。退会申請を進めてもよろしいでしょうか？
                    </div>
                    <p className="withdrawal-apply__reason_header">
                        退会理由（複数選択可）
                    </p>
                    <div className="withdrawal-apply__form">
                        <>
                            {WITHDRAWAL_REQUEST_REASONS_HEADLINE.map(
                                (group, i) => (
                                    <Fragment key={i}>
                                        <h3>{group.text}</h3>
                                        <ul>
                                            {group.content.map(
                                                (
                                                    id: WithdrawalRequestReason
                                                ) => (
                                                    <li key={id}>
                                                        <WithdrawalRequestReasonList
                                                            register={register}
                                                            id={id}
                                                        />
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </Fragment>
                                )
                            )}
                        </>
                        <h3>
                            ご意見
                            <small>
                                今後のサービス改善に役立ててまいります
                            </small>
                        </h3>
                        <textarea
                            name="opinion"
                            ref={register}
                            className="input-textarea"
                            placeholder="300文字以内でご入力ください。"
                        />
                        <div className="withdrawal-apply__opinion_count">
                            {[...opinionValue].length}/300
                        </div>
                        {isEmptyList(reasonsValue) && (
                            <div className="form-error">
                                退会理由が選択されていません
                            </div>
                        )}
                        {!isEmptyString(errors?.opinion?.message) && (
                            <div className="form-error">
                                {errors?.opinion?.message}
                            </div>
                        )}
                        <SmartButton
                            onClick={props.onClickConfirmModal}
                            disabled={!isDirty || !isValid}
                            isLoading={props.isLoading}
                        >
                            退会申請する
                        </SmartButton>
                    </div>
                </div>
            </MainLayout>
            <Modal
                onClickModalClose={props.onClickModalClose}
                isShow={props.isShowModal}
            >
                <div>
                    進行中のやりとりはすべて中止・キャンセルされますが、本当によろしいでしょうか？
                </div>
                <form onSubmit={handleSubmit(props.onSubmitWithdrawalApply)}>
                    <SmartButton type="submit" isLoading={props.isLoading}>
                        退会申請する
                    </SmartButton>
                </form>
                <SmartButton mode="cancel" onClick={props.onClickModalClose}>
                    キャンセル
                </SmartButton>
            </Modal>
        </>
    );
};

interface WithdrawalRequestReasonSectionProps {
    register: any;
    id: WithdrawalRequestReason;
}

const WithdrawalRequestReasonList = (
    props: WithdrawalRequestReasonSectionProps
) => (
    <label className="input-checkbox-label">
        <input
            ref={props.register}
            type="checkbox"
            name="reasons"
            value={props.id}
            className="input-checkbox"
        />
        {
            WITHDRAWAL_REQUEST_REASONS.find((reason) => reason.id === props.id)
                .text
        }
    </label>
);

export default WithdrawalApply;
